import { ChevronDown, SparklesIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePermissions } from "@/hooks/usePermissions";
import { tagNameToColor } from "@/lib/miscellaneous/tags";
import { cn } from "@/lib/utils";
import { noop, remove } from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AutoBuchungTag } from "../tags/auto-buchung";

type Props = {
  options: { label: string; value: string }[];
  values: string[];
  disabled?: boolean;
  error?: any;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  onSelect?: (values: string[]) => void;
  children?: ({
    values,
  }: {
    values: { label: string | undefined; value: string }[];
  }) => React.ReactNode;
};

export const TagsSelect = ({
  options,
  values,
  onSelect = noop,
  disabled = false,
  error,
  className,
  children,
}: Props) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(values);
  const { isSuperAdmin } = usePermissions();

  useEffect(() => {
    setSelected(values);
  }, [values, error]);

  const handleSelect = (value: string) => {
    const selectedValues = [...selected];
    let _values;

    if (selected.find((tag) => tag === value)) {
      remove(selectedValues, (tag) => tag === value);
      _values = selectedValues;
    } else {
      const values = [...selectedValues, value];
      _values = values;
    }

    setSelected(_values);
    onSelect(_values);
  };

  const enrichedSelected = selected.map((value) => {
    const label = options.find((option) => option.value === value)?.label;

    return {
      value,
      label,
    };
  });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={disabled}
        asChild
        onClick={(event) => event.stopPropagation()}
      >
        {children ? (
          children({ values: enrichedSelected })
        ) : (
          <div
            className={cn(
              "flex gap-1 items-center bg-background border rounded p-1 py-1.5 cursor-pointer h-10",
              className,
            )}
          >
            <div className="flex-1 flex-warp">
              {selected.length ? (
                enrichedSelected.map(({ value, label }) => {
                  if (!label) {
                    return null;
                  }

                  if (label === "Auto-Buchung") {
                    return <AutoBuchungTag key={value} />;
                  }

                  return (
                    <Badge
                      className="text-xs m-1"
                      key={value}
                      style={{
                        backgroundColor: tagNameToColor(label).backgroundColor,
                        color: tagNameToColor(label).textColor,
                      }}
                    >
                      {label}
                    </Badge>
                  );
                })
              ) : (
                <p className="text-primary text-sm p-1">{t("tags.empty")}</p>
              )}
            </div>
            <ChevronDown size={16} />
          </div>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-full">
        {options.length > 0 ? (
          options
            ?.filter(({ label }) => {
              if (label === "Auto-Buchung" && !isSuperAdmin) return false;
              return true;
            })
            .map(({ value, label }) => {
              if (label === "Auto-Buchung") {
                return (
                  <DropdownMenuCheckboxItem
                    onClick={(event) => {
                      event.stopPropagation();
                      handleSelect(value);
                    }}
                    key={value}
                    checked={selected.includes(value)}
                  >
                    <AutoBuchungTag />
                  </DropdownMenuCheckboxItem>
                );
              }

              return (
                <DropdownMenuCheckboxItem
                  onClick={(event) => {
                    event.stopPropagation();
                    handleSelect(value);
                  }}
                  key={value}
                  checked={selected.includes(value)}
                >
                  <Badge
                    className="text-xs m-1"
                    key={value}
                    style={{
                      backgroundColor: tagNameToColor(label).backgroundColor,
                      color: tagNameToColor(label).textColor,
                    }}
                  >
                    {label}
                  </Badge>
                </DropdownMenuCheckboxItem>
              );
            })
        ) : (
          <DropdownMenuLabel className="text-muted-foreground">{`${t("tags.not_available")}`}</DropdownMenuLabel>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
